"use client"

import { memo, useState } from "react"
import { toast } from "react-toastify"
import MainLayout from "@/layout/MainLayout"
import axiosInstance from "@/lib/axios"
import { getApiErrorMessage } from "@/lib/apiError"
import {
    ACCOUNT_FIELD_ERRORS,
    isValidEmail,
    isValidPersonName,
    normalizeEmail,
    normalizePersonName,
} from "@/validations/accountFields"

function buildOffices({ email, phoneDisplay }) {
    return [
        {
            city: 'पुणे (मुख्यालय)',
            addr: '४०२, प्रेस कॉम्प्लेक्स, FC रोड, शिवाजीनगर, पुणे ४११ ००५',
            phone: phoneDisplay,
            email,
        },
        {
            city: 'मुंबई',
            addr: '२०१, मीडिया हाऊस, लोअर परेल, मुंबई ४०० ०१३',
            phone: phoneDisplay,
            email,
        },
        {
            city: 'नागपूर',
            addr: '१०१, प्रेस भवन, सीताबर्डी, नागपूर ४४० ०१२',
            phone: phoneDisplay,
            email,
        },
    ]
}

function buildContacts(email) {
    return [
        { dept: 'बातम्या व संपादकीय', email, desc: 'बातम्यांच्या टिप्स, प्रेस रिलीज, संपादकीय प्रश्न' },
        { dept: 'जाहिरात', email, desc: 'जाहिरात दर, मीडिया किट, कस्टम कॅम्पेन' },
        { dept: 'तांत्रिक सहाय्य', email, desc: 'वेबसाइट समस्या, खाते व्यवस्थापन' },
        { dept: 'कायदेशीर', email, desc: 'DMCA, गोपनीयता विनंत्या, कायदेशीर नोटिसा' },
        { dept: 'फॅक्ट चेक', email, desc: 'चुकीची माहिती नोंदवा, दुरुस्तीच्या विनंत्या' },
        { dept: 'HR / करिअर', email, desc: 'नोकरीच्या संधी, इंटर्नशिप' },
    ]
}

const SUBJECTS = [
    'बातम्यांची टीप द्या',
    'जाहिरातीबद्दल चौकशी',
    'चुकीची माहिती नोंदवा',
    'तांत्रिक समस्या',
    'इतर',
]

const ContactUs = ({ siteContact = {} } = {}) => {
    const email = siteContact.email || ''
    const phoneDisplay = siteContact.phoneDisplay || ''
    const OFFICES = buildOffices({ email, phoneDisplay })
    const CONTACTS = buildContacts(email)
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        subject: SUBJECTS[0],
        message: '',
    })
    const [submitting, setSubmitting] = useState(false)

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const name = normalizePersonName(form.name)
        const email = normalizeEmail(form.email)
        const message = String(form.message || '').trim()

        if (!name || !isValidPersonName(name)) {
            toast.error('Please enter a valid name')
            return
        }
        if (!isValidEmail(email)) {
            toast.error(ACCOUNT_FIELD_ERRORS.emailFormat)
            return
        }
        if (message.length < 5) {
            toast.error('Please enter your message')
            return
        }

        setSubmitting(true)
        try {
            const res = await axiosInstance.post('/contact', {
                name,
                email,
                phone: form.phone,
                subject: form.subject,
                message,
                source: 'contact',
            })
            if (res.data?.success) {
                toast.success(res.data.message || 'Message sent successfully')
                setForm({
                    name: '',
                    email: '',
                    phone: '',
                    subject: SUBJECTS[0],
                    message: '',
                })
            } else {
                toast.error(res.data?.error || 'Unable to send message')
            }
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Unable to send message'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <MainLayout>
            <div className="border-b-4 border-(--brand-primary) pb-8 mb-12">
                <div className="mr text-[11px] font-bold tracking-widest text-(--text-tertiary) uppercase mb-3">
                    कंपनी · COMPANY
                </div>

                <h1 className="mr m-0 mb-3 text-[clamp(28px,4vw,48px)] font-extrabold leading-[1.1] tracking-[-0.02em]">
                    संपर्क करा
                </h1>

                <p className="mr m-0 text-[clamp(14px,1.3vw,17px)] leading-[1.7] text-(--text-secondary)">
                    आमच्याशी बातम्या, जाहिरात, तांत्रिक समस्या किंवा कोणत्याही विषयावर संपर्क साधा. आम्ही २४ तासांत उत्तर देतो.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
                <div>
                    <h2 className="mr text-[20px] font-extrabold mb-6 text-(--brand-primary)">
                        संदेश पाठवा
                    </h2>

                    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
                        <div>
                            <label className="mr block text-[13px] font-semibold mb-1.5 text-(--text-primary)">
                                पूर्ण नाव
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="तुमचे नाव"
                                required
                                className="w-full px-3 py-2.5 border border-(--border-default) text-sm font-inherit rounded outline-none box-border bg-white"
                            />
                        </div>

                        <div>
                            <label className="mr block text-[13px] font-semibold mb-1.5 text-(--text-primary)">
                                ईमेल पत्ता
                            </label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                placeholder="email@example.com"
                                required
                                className="w-full px-3 py-2.5 border border-(--border-default) text-sm font-inherit rounded outline-none box-border bg-white"
                            />
                        </div>

                        <div>
                            <label className="mr block text-[13px] font-semibold mb-1.5 text-(--text-primary)">
                                फोन नंबर (ऐच्छिक)
                            </label>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                placeholder="+91 98765 43210"
                                className="w-full px-3 py-2.5 border border-(--border-default) text-sm font-inherit rounded outline-none box-border bg-white"
                            />
                        </div>

                        <div>
                            <label className="mr block text-[13px] font-semibold mb-1.5 text-(--text-primary)">
                                विषय
                            </label>
                            <select
                                value={form.subject}
                                onChange={(e) => handleChange('subject', e.target.value)}
                                className="w-full px-3 py-2.5 border border-(--border-default) text-sm font-inherit rounded outline-none bg-white"
                            >
                                {SUBJECTS.map((subject) => (
                                    <option key={subject} value={subject} className="mr">
                                        {subject}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="mr block text-[13px] font-semibold mb-1.5 text-(--text-primary)">
                                संदेश
                            </label>
                            <textarea
                                value={form.message}
                                onChange={(e) => handleChange('message', e.target.value)}
                                placeholder="तुमचा संदेश येथे लिहा..."
                                rows={5}
                                required
                                className="w-full px-3 py-2.5 border border-(--border-default) text-sm font-inherit rounded outline-none resize-y box-border"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-7 py-3 bg-(--brand-primary) text-white border-0 font-bold text-[15px] cursor-pointer rounded w-full sm:w-fit disabled:opacity-60"
                        >
                            {submitting ? 'पाठवत आहे...' : 'संदेश पाठवा →'}
                        </button>
                    </form>
                </div>

                <div>
                    <h2 className="mr text-[20px] font-extrabold mb-6 text-(--brand-primary)">
                        विभागानुसार संपर्क
                    </h2>

                    <div className="flex flex-col gap-4">
                        {CONTACTS.map((c) => (
                            <div
                                key={c.dept}
                                className="p-4 border border-(--border-default) border-l-[3px] border-l-(--brand-primary)"
                            >
                                <div className="mr text-sm font-bold mb-1">
                                    {c.dept}
                                </div>

                                {c.email ? (
                                    <a
                                        href={`mailto:${c.email}`}
                                        className="inline-block text-[13px] text-(--brand-primary) mb-1 hover:underline"
                                    >
                                        {c.email}
                                    </a>
                                ) : null}

                                <div className="mr text-xs text-(--text-tertiary)">
                                    {c.desc}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mb-12">
                <h2 className="mr text-[20px] font-extrabold mb-6 text-(--brand-primary)">
                    आमची कार्यालये
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {OFFICES.map((o) => (
                        <div
                            key={o.city}
                            className="p-5 border border-(--border-default)"
                        >
                            <div className="mr text-[15px] font-extrabold text-(--brand-primary) mb-2.5">
                                {o.city}
                            </div>

                            <div className="mr text-[13px] leading-[1.6] text-(--text-secondary) mb-2">
                                {o.addr}
                            </div>

                            {o.phone ? (
                                <a
                                    href={`tel:${o.phone.replace(/\s+/g, '')}`}
                                    className="block text-[13px] text-(--brand-primary) mb-1 hover:underline"
                                >
                                    {o.phone}
                                </a>
                            ) : null}

                            {o.email ? (
                                <a
                                    href={`mailto:${o.email}`}
                                    className="block text-[13px] text-(--brand-primary) hover:underline"
                                >
                                    {o.email}
                                </a>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        </MainLayout>
    )
}

export default memo(ContactUs)
